import { API } from 'aws-amplify';
import { getChannelAndSubchannelQueries } from './utils';
import {
  fetchedJobs,
  startFetchingJobs,
  fetchedJob,
  startFetchingJob,
  startFetchingPodcasts,
  fetchedPodcasts,
  fetchedUrls,
  startFetchingUrls,
  fetchedUserInfo,
  startFetchingUserInfo,
  fetchedUsers,
  startFetchingUsers,
  fetchedGroups,
  fetchedReplacements,
  startFetchingReplacements,
  fetchedDocuments,
  fetchedPodcast,
  startFetchingDocuments,
  startFetchingPodcast,
  startFetchingGroups,
} from '../redux/actions';
import { AppDispatch } from '../redux/store';
import { ReplacementType, SpeechParamsType, ImagesOfDocument, DocumentType } from '../types/AppTypes';

const apiName = 'Columbia';

const callAnalyticsApi = async (endPoint: string, start: Date, end: Date, channels: string[], subchannels: string[]) => {
  const [channelsQuery, subchannelsQuery] = getChannelAndSubchannelQueries(channels, subchannels);

  const res = await API.get(
    apiName,
    `/analytics/report/${endPoint}?startDay=${start.toISOString().split('T')[0]}&endDay=${
      end.toISOString().split('T')[0]
    }&start=0${channelsQuery}${subchannelsQuery}`,
    null,
  );
  return res;
};

export const getDocuments = async (start: Date, end: Date, timezoneOffset: string) => {
  const res = await API.get(
    apiName,
    `/document?createdFrom=${start.toISOString().split('T')[0]}&createdTo=${
      end.toISOString().split('T')[0]
    }&timezone=${timezoneOffset}`,
    null,
  );
  return res;
};

export const updateDocument = async (
  documentId: string,
  text: string | undefined,
  externalUrl: string | undefined,
  shouldCreateImageUploadUrls: boolean | undefined,
  imageDetails: ImagesOfDocument[] | undefined,
  title: string | undefined,
  description: string | undefined,
  publicationYear: number | undefined,
  publicationLocation: string | undefined,
  author: string | undefined,
  publisher: string | undefined,
) => {
  const init = {
    body: {
      text,
      externalUrl,
      shouldCreateImageUploadUrls,
      imageDetails,
      title,
      description,
      publicationYear,
      publicationLocation,
      author,
      publisher,
    },
    headers: { 'Content-Type': 'application/json' },
  };
  const res = await API.put(apiName, `/document/${documentId}/text`, init);
  return res;
};

export const deleteEpisodes = async (documentsArray: string[]) => {
  const init = {
    headers: { 'Content-Type': 'application/json' },
  };
  const deleteUrl = '/document?id=' + documentsArray.join('&id=');
  const res = await API.del(apiName, deleteUrl, init);
  return res;
};

export const publishDocument = async (documentId: string) => {
  const init = {
    body: { state: 'PUBLISHED' },
    headers: { 'Content-Type': 'application/json' },
  };
  const res = await API.put(apiName, `/document/${documentId}`, init);
  return res;
};

export const updateDocumentVoice = async (documentId: string, data: SpeechParamsType) => {
  const init = {
    body: { speechParams: data },
    headers: { 'Content-Type': 'application/json' },
  };
  const res = await API.put(apiName, `/document/${documentId}`, init);
  const status = res.status;
  return res;
};

export const getChannels = async (startDate: Date, endDate: Date) => {
  const res = await API.get(
    apiName,
    `/analytics/channels?startDay=${startDate.toISOString().split('T')[0]}&endDay=${endDate.toISOString().split('T')[0]}`,
    null,
  );
  return res;
};

export const getSubChannels = async (startDate: Date, endDate: Date, channels: string[]) => {
  let queryStr = '';
  channels.forEach((c) => (queryStr += `&channel=${c}`));
  const res = await API.get(
    apiName,
    `/analytics/subchannels?startDay=${startDate.toISOString().split('T')[0]}&endDay=${
      endDate.toISOString().split('T')[0]
    }${queryStr}`,
    null,
  );
  return res;
};

export const getTopDocuments = (start: Date, end: Date, channels: string[], subchannels: string[]) => {
  return callAnalyticsApi('topDocuments', start, end, channels, subchannels);
};

export const getTopUsers = (start: Date, end: Date, channels: string[], subchannels: string[]) => {
  return callAnalyticsApi('topUsers', start, end, channels, subchannels);
};

export const getTopChannels = async (start: Date, end: Date, channels: string[], subchannels: string[]) => {
  return callAnalyticsApi('topChannels', start, end, channels, subchannels);
};

export const getDailyResults = async (start: Date, end: Date, channels: string[], subchannels: string[]) => {
  return callAnalyticsApi('monthResults', start, end, channels, subchannels);
};

export const fetchDocuments = (dispatch: AppDispatch, timezoneOffset: string, start?: Date | null, end?: Date | null) => {
  const to = end || new Date();
  const from = start || new Date(new Date().setDate(new Date().getDate() - 7));

  dispatch(startFetchingDocuments());

  getDocuments(from, to, timezoneOffset)
    .then((response) => {
      dispatch(fetchedDocuments(response));
    })
    .catch((error) => {
      dispatch(fetchedDocuments(null, 'Error retrieving data', true));
    });
};

export const getPublishedDocuments = async (start: Date, end: Date, channels: string[], subchannels: string[]) => {
  return callAnalyticsApi('publishedDocuments', start, end, channels, subchannels);
};

export const getReplacements = async (): Promise<DocumentType[]> => {
  const res = await API.get(apiName, '/replacement/en', null);
  return res;
};

export const fetchReplacements = (dispatch: AppDispatch) => {
  dispatch(startFetchingReplacements());

  getReplacements()
    .then((response) => {
      dispatch(fetchedReplacements(response));
    })
    .catch((error) => {
      dispatch(fetchedReplacements(null, 'Error retrieving data', true));
    });
};

export const createUpdateReplacement = async (oldRepl: ReplacementType, newRepl: ReplacementType) => {
  const init = {
    body: { oldReplacement: oldRepl, newReplacement: newRepl },
    headers: { 'Content-Type': 'application/json' },
  };
  try {
    const res = await API.put(apiName, '/replacement/en', init);
    return res;
  } catch (error) {
    return { error: 'Failed to update' };
  }
};

export const deleteReplacement = async (pattern: string) => {
  try {
    const res = await API.del(apiName, `/replacement/${encodeURIComponent(pattern)}`, null);
    return res;
  } catch (error) {
    return { error: 'Failed to delete' };
  }
};

export const getJobs = async () => {
  const res = await API.get(apiName, '/job', null);
  return res;
};

export const fetchJobs = (dispatch: AppDispatch) => {
  dispatch(startFetchingJobs());

  getJobs()
    .then((response) => {
      dispatch(fetchedJobs(response));
    })
    .catch((error) => {
      dispatch(fetchedJobs(null, 'Error retrieving data', true));
    });
};

export const getJob = async (jobId: string) => {
  const res = await API.get(apiName, `/job/${jobId}`, null);
  return res;
};

export const fetchJob = (dispatch: AppDispatch, jobId: string) => {
  dispatch(startFetchingJob());

  getJob(jobId)
    .then((response) => {
      dispatch(fetchedJob(response));
    })
    .catch((error) => {
      dispatch(fetchedJob(null, 'Error retrieving data', true));
    });
};

export const deleteJob = async (jobId: string) => {
  const res = await API.del(apiName, `/job/${jobId}`, null);
  return res;
};

export const addJob = async (name: string, rssUrl: string, customerId: string, schedule: string | undefined) => {
  const init = {
    body: { name: name, rssUrl: rssUrl, customerId: customerId, schedule: schedule },
    headers: { 'Content-Type': 'application/json' },
  };
  const res = await API.post(apiName, `/job`, init);
  return res;
};

export const getPodcasts = async () => {
  const res = await API.get(apiName, '/podcast', null);
  return res;
};

export const fetchPodcasts = (dispatch: AppDispatch) => {
  dispatch(startFetchingPodcasts());

  getPodcasts()
    .then((response) => {
      dispatch(fetchedPodcasts(response));
    })
    .catch((error) => {
      dispatch(fetchedPodcasts(null, 'Error retrieving data', true));
    });
};

export const deletePodcast = async (podcastId: string) => {
  const res = await API.del(apiName, `/podcast/${podcastId}`, null);
  return res;
};

export const addPodcast = async (formData: FormData) => {
  const init = {
    body: formData,
    headers: { 'Content-type': 'multipart/form-data' },
  };
  const res = await API.post(apiName, `/podcast`, init);
  return res;
};

export const getPodcastId = async (podcastId: string) => {
  const res = await API.get(apiName, `/podcast/${podcastId}`, null);
  return res;
};

export const fetchPodcastId = (dispatch: AppDispatch, podcastId: string) => {
  dispatch(startFetchingPodcast());

  getPodcastId(podcastId)
    .then((response) => {
      dispatch(fetchedPodcast(response));
    })
    .catch((error) => {
      dispatch(fetchedPodcast(null, 'Error retrieving data', true));
    });
};

export const updatePodcast = async (id: string, formData: FormData) => {
  const params = {
    body: formData,
    headers: { 'Content-type': 'multipart/form-data' },
  };
  const res = await API.put(apiName, `/podcast/${id}`, params);
  return res;
};

export const getUrls = async () => {
  //const res = await API.get(apiName, '/podcast', null);
  return null;
};

export const addUrl = async (formData: FormData) => {
  const init = {
    body: formData,
    headers: { 'Content-type': 'multipart/form-data' },
  };
  //const res = await API.post(apiName, `/podcast`, init);
  return null;
};

export const fetchUrls = (dispatch: AppDispatch) => {
  dispatch(startFetchingUrls());

  getUrls()
    .then((response) => {
      dispatch(fetchedUrls(response));
    })
    .catch((error) => {
      dispatch(fetchedUrls(null, 'Error retrieving data', true));
    });
};

export const getUserInfo = async () => {
  const res = await API.get(apiName, '/user', null);
  return res;
};

export const fetchUserInfo = (dispatch: AppDispatch) => {
  dispatch(startFetchingUserInfo());

  getUserInfo()
    .then((response) => {
      dispatch(fetchedUserInfo(response));
    })
    .catch((error) => {
      dispatch(fetchedUserInfo(null, 'Error retrieving data', true));
    });
};

export const getUsers = async () => {
  const res = await API.get(apiName, '/users', null);
  return res;
};

export const fetchUsers = (dispatch: AppDispatch) => {
  dispatch(startFetchingUsers());

  getUsers()
    .then((response) => {
      dispatch(fetchedUsers(response));
    })
    .catch((error) => {
      dispatch(fetchedUsers(null, 'Error retrieving data', true));
    });
};

export const getGroups = async () => {
  const res = await API.get(apiName, '/groups', null);
  return res;
};

export const fetchGroups = (dispatch: AppDispatch) => {
  dispatch(startFetchingGroups());

  getGroups()
    .then((response) => {
      dispatch(fetchedGroups(response));
    })
    .catch((error) => {
      dispatch(fetchedGroups(null, 'Error retrieving data', true));
    });
};

export const addGroup = async (groupId: string, actions: string[]) => {
  const init = {
    body: { groupId: groupId, actions: actions },
    headers: { 'Content-Type': 'application/json' },
  };
  const res = await API.post(apiName, `/groups`, init);
  return res;
};

export const updateGroup = async (groupId: string, actions: string[]) => {
  const init = {
    body: { actions },
    headers: { 'Content-Type': 'application/json' },
  };
  const res = await API.put(apiName, `/groups/${groupId}`, init);
  return res;
};

export const addUser = async (username: string, groups: string[]) => {
  const init = {
    body: { username: username, customers: [], groups: groups },
    headers: { 'Content-Type': 'application/json' },
  };
  const res = await API.post(apiName, `/users`, init);
  return res;
};

export const updateUser = async (username: string, groups: string[]) => {
  const init = {
    body: { groups },
    headers: { 'Content-Type': 'application/json' },
  };
  const res = await API.put(apiName, `/users/${username}`, init);
  return res;
};

export const deleteImages = async (imageId: string, documentId: string) => {
  const res = await API.del(apiName, `/document/${documentId}/images/${imageId}`, null);
  return res;
};
